/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import _, { flatten, get, isEqual, uniq, values } from 'lodash';

import styles from './styles.module.scss';
import searchImg from '../../assets/images/searchRed.svg';
import ActionButtons from '../../components/ActionButtons';
import Aggregation from '../../components/Aggregation';
import Button from '../../components/Button';
import Checkbox from '../../components/Checkbox';
import DataLoader from '../../components/DataLoader';
import Modal from '../../components/Modal';
import Table from '../../components/Table';
import Sidebar, { SidebarSection } from '../../components/Sidebar';
import IconButton from '../../components/IconButton';
import TrackListHeader from '../../components/TrackListHeader';
import TrackListRow from '../../components/TrackListRow';
import TrackSearchQuery from '../../components/TrackSearchQuery';
import WorklistBasicForm from '../../forms/WorklistBasicForm';
import actions from '../../redux/actions';
import { getCategories } from '../../redux/reducers/categories';
import { getIsPlaying } from '../../redux/reducers/playerQueue';
import {
  getAggregations,
  getExpandedTrackIds,
  getHasMoreResults,
  getSelectedTrackIds,
  getSubmittedTrackSearchParams,
  getTotal,
  getTrackList,
  getTrackSearchParams,
} from '../../redux/reducers/trackSearch';
import { getSubmittedSearchParams } from '../../redux/reducers/app';
import { getSessionUsageId } from '../../redux/reducers/usages';
import { getWorklist1, getWorklist2, getWorklists } from '../../redux/reducers/worklists';
import protonesUtils from '../../utils/protones';
import stringUtils from '../../utils/string';
import InputSelect from '../../components/InputSelect';

import type {
  Aggregations,
  Category,
  Fetchable,
  ReactRouterProps,
  ReduxState,
  SearchParams,
  SimplifiedMedia,
  SimplifiedTrack,
  SimplifiedWorklist,
  Track,
  TrackAggregationKey,
  TrackSearchFullParams,
  TrackSortingField,
  Translate,
  WorklistItem,
} from '../../types';
import { searchParamsProps } from '../../types/SearchParams';
import { withKeycloak } from '@react-keycloak/web';
import * as Keycloak from 'keycloak-js';

type Props = ReactRouterProps & {
  keycloak: Keycloak,
  params: TrackSearchFullParams,
  submittedParams: TrackSearchFullParams,
  submittedCommonParams: SearchParams,
  tracks: Fetchable<Track[]>,
  categories: Fetchable<Category[]>,
  aggregations: Fetchable<Aggregations<TrackAggregationKey>>,
  worklists: Fetchable<SimplifiedWorklist[]>,
  worklist1: Fetchable<SimplifiedWorklist>,
  worklist2: Fetchable<SimplifiedWorklist>,
  sessionUsageId: Fetchable<number>,
  total: Fetchable<number>,
  hasMoreResults: boolean,
  currentTrackId: number,
  isPlaying: boolean,
  pageScroll: number,
  selectedTracks: {
    [trackId: number]: boolean,
  },
  selectedTrackIds: number[],
  expandedTracks: {
    [trackId: number]: boolean,
  },
  expandedTrackIds: number[],
  playTracks: typeof actions.playTracks,
  downloadTracks: typeof actions.downloadTracks,
  askQuotation: typeof actions.askQuotation,
  askTracks: typeof actions.askTracks,
  createCartItemsFromTracks: typeof actions.createCartItemsFromTracks,
  createWorklistItems: typeof actions.createWorklistItems,
  saveWorklistItemsForCheckout: typeof actions.saveWorklistItemsForCheckout,
  saveWorklist: typeof actions.saveWorklist,
  setTrackSearch: typeof actions.setTrackSearch,
  performTrackSearch: typeof actions.performTrackSearch,
  fetchTrackSearchPage: typeof actions.fetchTrackSearchPage,
  displayMessage: typeof actions.displayMessage,
  askTracks: typeof actions.askTracks,
  trackSearchSetPageScroll: typeof actions.trackSearchSetPageScroll,
  trackSearchExpandTrack: typeof actions.trackSearchExpandTrack,
  trackSearchToggleSelectTracks: typeof actions.trackSearchToggleSelectTracks,
  trackSearchToggleExpandSelectedTracks: typeof actions.trackSearchToggleExpandSelectedTracks,
  t: Translate,
};

const mapStateToProps = (state: ReduxState) => {
  return {
    submittedParams: getSubmittedTrackSearchParams(state),
    params: getTrackSearchParams(state),
    submittedCommonParams: getSubmittedSearchParams(state),
    tracks: getTrackList(state),
    total: getTotal(state),
    categories: getCategories(state),
    aggregations: getAggregations(state),
    hasMoreResults: getHasMoreResults(state),
    currentTrackId: state.playerQueue.currentTrackId,
    isPlaying: getIsPlaying(state),
    worklists: getWorklists(state),
    worklist1: getWorklist1(state),
    worklist2: getWorklist2(state),
    sessionUsageId: getSessionUsageId(state),
    pageScroll: state.trackSearch.pageScroll,
    selectedTracks: state.trackSearch.selectedTracks,
    selectedTrackIds: getSelectedTrackIds(state),
    expandedTracks: state.trackSearch.expandedTracks,
    expandedTrackIds: getExpandedTrackIds(state),
  };
};

type State = {
  isCreateWorklistModalVisible: boolean,
  isSelectWorklistModalVisible: boolean,
  worklists: SimplifiedWorklist[],
  selectWorklistTracks: Track[],
  selectWorklistSessionUsageId: number,
  selectWorklistSelectedId: number,
  createWorklistWithItemsAction: ((name: string, description: string) => mixed) | null,
  tagsSearchText: string,
};

class Search extends React.Component<Props, State> {
  state = {
    isCreateWorklistModalVisible: false,
    isSelectWorklistModalVisible: false,
    createWorklistWithItemsAction: null,
    worklists: [],
    selectWorklistTracks: [],
    selectWorklistSessionUsageId: 0,
    selectWorklistSelectedId: 0,
    tagsSearchText: '',
  };

  scrollContainer: any = React.createRef();
  pageScroll: number = 0;

  componentDidMount() {
    if (
      !_.isEqual(
        this.props.submittedCommonParams,
        _.pick(this.props.submittedParams, searchParamsProps)
      ) ||
      _.isNil(this.props.tracks.data)
    ) {
      this._handleRefreshTrackSearch();
    }
    if (this.scrollContainer.current) {
      this.scrollContainer.current.scrollTo(0, this.props.pageScroll);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!isEqual(this.props.submittedParams, prevProps.submittedParams)) {
      this._handleRefreshTrackSearch();
    }
  }

  componentWillUnmount() {
    this.props.trackSearchSetPageScroll(this.pageScroll);
  }

  _handlePageScroll = (event: any) => {
    const element = event.target;
    const pageScroll = element.scrollTop;
    this.pageScroll = pageScroll;
  };

  _handleRefreshTrackSearch = () => {
    this.props.performTrackSearch(this.props.params);
  };

  _handleLoadMore = () => {
    const start = this.props.tracks.data ? this.props.tracks.data.length : 0;
    const end = start + 30;
    this.props.fetchTrackSearchPage({
      ...this.props.params,
      pagination: {
        start,
        end,
      },
    });
  };

  _addFilterAndSubmit = (filter: TrackAggregationKey, value: any) => {
    const { performTrackSearch, setTrackSearch, params } = this.props;
    let { filters } = params;
    if (filters == null) {
      filters = { [filter]: [value] };
    } else if (filters[filter] == null) {
      filters[filter] = [value];
    } else {
      filters[filter] = uniq([...filters[filter], value]);
    }
    const nextParams = {
      ...params,
      filters,
    };
    setTrackSearch(nextParams);
    performTrackSearch(nextParams);
    return nextParams;
  };

  _removeFilterAndSubmit = (filter: TrackAggregationKey, value: any) => {
    const { performTrackSearch, setTrackSearch, params } = this.props;
    let { filters } = params;
    if (filters == null || filters[filter] == null || !filters[filter].includes(value)) {
      return params;
    }
    filters = filters || {};
    filters = {
      ...filters,
      [filter]: (filters[filter] || []).filter(val => val !== value),
    };
    const nextParams = {
      ...params,
      filters,
    };
    setTrackSearch(nextParams);
    performTrackSearch(nextParams);
    return nextParams;
  };

  _removeFilterGroupAndSubmit = (filter: TrackAggregationKey) => {
    const { performTrackSearch, setTrackSearch, params } = this.props;
    let { filters } = params;
    filters = _.omit(filters, filter) || {};
    const nextParams = {
      ...params,
      filters,
    };
    setTrackSearch(nextParams);
    performTrackSearch(nextParams);
    return nextParams;
  };

  _addFilterGroupAndSubmit = (filter: TrackAggregationKey) => {
    const { performTrackSearch, setTrackSearch, aggregations, params } = this.props;
    const buckets = _.get(aggregations, `data.${filter}.buckets`, []);
    const filterValues = buckets.map(b => b.key);
    const filters = {
      ...(params.filters || {}),
      [filter]: filterValues,
    };
    const nextParams = {
      ...params,
      filters,
    };
    setTrackSearch(nextParams);
    performTrackSearch(nextParams);
    return nextParams;
  };

  _getIsFilterActive = (filter: TrackAggregationKey, value: any) => {
    const { params } = this.props;
    let { filters } = params;
    if (filters == null || filters[filter] == null || !filters[filter].includes(value)) {
      return false;
    }
    return true;
  };

  _getIsFilterGroupActive = (filter: TrackAggregationKey) => {
    const { params } = this.props;
    let { filters } = params;
    if (filters == null || filters[filter] == null || filters[filter].length === 0) {
      return false;
    }
    return true;
  };

  _toggleFilterAndSubmit = (filter: TrackAggregationKey, value: any) => {
    const isFilterActive = this._getIsFilterActive(filter, value);
    if (isFilterActive) {
      return this._removeFilterAndSubmit(filter, value);
    } else {
      return this._addFilterAndSubmit(filter, value);
    }
  };

  _toggleFilterGroupAndSubmit = (filter: TrackAggregationKey) => {
    const isFilterGroupActive = this._getIsFilterGroupActive(filter);
    if (isFilterGroupActive) {
      return this._removeFilterGroupAndSubmit(filter);
    } else {
      return this._addFilterGroupAndSubmit(filter);
    }
  };

  _toggleExcludeProductionMusic = () => {
    const { performTrackSearch, setTrackSearch, params } = this.props;
    const nextParams = {
      ...params,
      excludeProductionMusic: !params.excludeProductionMusic,
    };
    setTrackSearch(nextParams);
    performTrackSearch(nextParams);
  };

  _resetFiltersAndSubmit = () => {
    const { setTrackSearch, performTrackSearch, params } = this.props;
    const nextParams = {
      ...params,
      filters: {},
      excludeProductionMusic: false,
    };
    setTrackSearch(nextParams);
    performTrackSearch(nextParams);
    this.setState({ tagsSearchText: '' });
  };

  _setFiltersAndSubmit = (filters: { [TrackAggregationKey]: any[] }) => {
    const { performTrackSearch, setTrackSearch, params } = this.props;
    const nextParams = {
      ...params,
      filters,
    };
    setTrackSearch(nextParams);
    performTrackSearch(nextParams);
  };

  _setFilterAndSubmit = (filter: TrackAggregationKey, values: any[]) => {
    const { performTrackSearch, setTrackSearch, params } = this.props;
    let { filters } = params;
    if (filters != null) {
      filters[filter] = values;
    } else {
      filters = { [filter]: values };
    }
    const nextParams = {
      ...params,
      filters,
    };
    setTrackSearch(nextParams);
    performTrackSearch(nextParams);
  };

  _setSortAndSubmit = (field: TrackSortingField) => {
    const { performTrackSearch, setTrackSearch, params } = this.props;
    let sorting;
    if (params.sorting) {
      if (params.sorting.field === field) {
        if (params.sorting.order === 'asc') {
          sorting = { field, order: 'desc' };
        } else if (params.sorting.order === 'desc') {
          sorting = undefined;
        }
      } else {
        sorting = { field, order: 'asc' };
      }
    } else {
      sorting = { field, order: 'asc' };
    }
    const nextParams = {
      ...params,
      sorting,
    };
    setTrackSearch(nextParams);
    performTrackSearch(nextParams);
  };

  _increaseFilterGroupPagination = (filter: TrackAggregationKey) => {
    const { performTrackSearch, setTrackSearch, params } = this.props;
    const filterPagination = _.get(params, `filtersMeta.${filter}.pagination`, 10);
    const nextParams = _.merge({}, params);
    _.set(nextParams, `filtersMeta.${filter}.pagination`, filterPagination + 10);
    setTrackSearch(nextParams);
    performTrackSearch(nextParams);
  };

  _renderExcludeProductionMusic = () => {
    const checked = !!this.props.submittedParams.excludeProductionMusic;
    return (
      <div className={styles.aggregation}>
        <div className={styles.aggregationBuckets}>
          <div
            onClick={e => {
              this._toggleExcludeProductionMusic();
              e.preventDefault();
            }}
            className={styles.aggregationBucket}
          >
            <Checkbox
              small={true}
              value={'excludeProductionMusic'}
              label={this.props.t('EXCLUDE_PRODUCTION_MUSIC')}
              labelAfter={true}
              checked={checked}
            />
          </div>
        </div>
      </div>
    );
  };

  _renderAggregations = data => {
    const {
      submittedParams: { filters },
      t,
    } = this.props;
    const {
      tags,
      labels,
      performers,
      editors,
      authors,
      years,
      categories,
      catalogs,
      collectings,
      // producers
    } = data;
    const { tagsSearchText } = this.state;
    return (
      <div>
        {this._renderExcludeProductionMusic()}
        <Aggregation
          name="categories"
          label={t('CATEGORIES')}
          categories={this.props.categories.data || []}
          buckets={categories.buckets.filter(bucket => bucket.count > 0)}
          selectedBuckets={get(filters, 'categories', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          showAll={true}
        />
        <Aggregation
          name="performers"
          label={t('PERFORMER')}
          buckets={performers.buckets}
          selectedBuckets={get(filters, 'performers', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="authors"
          label={t('AUTHORS')}
          buckets={authors.buckets}
          selectedBuckets={get(filters, 'authors', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="labels"
          label={t('LABEL')}
          buckets={labels.buckets}
          selectedBuckets={get(filters, 'labels', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="catalogs"
          label={t('CATALOG')}
          buckets={catalogs.buckets}
          selectedBuckets={get(filters, 'catalogs', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="years"
          label={t('YEAR')}
          buckets={years.buckets}
          selectedBuckets={get(filters, 'years', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="editors"
          label={t('EDITORS')}
          buckets={editors.buckets}
          selectedBuckets={get(filters, 'editors', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="tags"
          label={t('TAGS')}
          buckets={tags.buckets}
          selectedBuckets={get(filters, 'tags', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          canSearchFilters={true}
          searchText={tagsSearchText}
          onSearchTextChange={text => this.setState({ tagsSearchText: text })}
        />
        <Aggregation
          name="collectings"
          label={t('COLLECTING')}
          buckets={collectings.buckets}
          selectedBuckets={get(filters, 'collectings', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
      </div>
    );
  };

  _handleMediaClick = (media: SimplifiedMedia) => {
    protonesUtils.goTo(this.props, protonesUtils.routes.medias.get(media.id));
  };

  _saveItemsForCheckout = (worklistItems: WorklistItem[], worklistId: number) => {
    this.props.saveWorklistItemsForCheckout(worklistItems, worklistId);
  };

  _requestCreateWorklistWithItems = (
    tracks: SimplifiedTrack[],
    usageId: number,
    shouldProceedToCheckout: boolean = false
  ) => {
    const { saveWorklist, createWorklistItems, t } = this.props;
    const createWorklistWithItemsAction = (name: string, description: string) =>
      saveWorklist(
        {
          name: name || t('WORKLIST'),
          description: description || t('NO_DESCRIPTION'),
          usage: protonesUtils.getUsageUpdateParamsFromObject({}),
        },
        async worklist => {
          await createWorklistItems(tracks, worklist, usageId, (worklistItems: WorklistItem[]) => {
            if (shouldProceedToCheckout) {
              this._saveItemsForCheckout(worklistItems, worklist.id);
              protonesUtils.goTo(this.props, protonesUtils.routes.worklists.get(worklist.id));
            }
          });
        }
      );
    this._showCreateWorklistModal(createWorklistWithItemsAction);
  };

  _requestSendItemsToWorklist = async (
    tracks: SimplifiedTrack[],
    worklist: any,
    usageId: any,
    shouldProceedToCheckout: boolean = false
  ) => {
    const { createWorklistItems } = this.props;
    await createWorklistItems(tracks, worklist, usageId, (worklistItems: WorklistItem[]) => {
      if (shouldProceedToCheckout) {
        this._saveItemsForCheckout(worklistItems, worklist.id);
        protonesUtils.goTo(this.props, protonesUtils.routes.worklists.get(worklist.id));
      }
    });
  };

  _showCreateWorklistModal = createWorklistWithItemsAction => {
    this.setState({ isCreateWorklistModalVisible: true, createWorklistWithItemsAction });
  };

  _dismissCreateWorklistModal = () => {
    this.setState({ isCreateWorklistModalVisible: false, createWorklistWithItemsAction: null });
  };

  _setSelectWorklistSelectedId = selectWorklistSelectedId => {
    this.setState({ selectWorklistSelectedId });
  };

  _showSelectWorklistModal = (worklists, tracks, sessionUsageId) => {
    this.setState({
      isSelectWorklistModalVisible: true,
      worklists,
      selectWorklistTracks: tracks,
      selectWorklistSessionUsageId: sessionUsageId,
    });
  };

  _dismissSelectWorklistModal = () => {
    this.setState({ isSelectWorklistModalVisible: false });
  };

  _handleCreateWorklistModalSubmit = worklistParams => {
    const { createWorklistWithItemsAction } = this.state;
    const { name, description } = worklistParams;
    this._dismissCreateWorklistModal();
    if (createWorklistWithItemsAction != null) {
      createWorklistWithItemsAction(name, description);
    }
  };

  _renderActionButtons = (tracks: Track[]) => {
    const {
      displayMessage,
      downloadTracks,
      sessionUsageId,
      worklist1,
      worklist2,
      worklists,
      t,
    } = this.props;
    const downloadable = tracks.every(track => track.downloadable === true);
    return (
      <ActionButtons
        download={{
          fetchable: downloadable,
          fetching: false,
          data: () =>
            downloadable
              ? downloadTracks(tracks)
              : displayMessage(t('TRACK_NOT_DOWNLOADABLE'), false),
        }}
        buy={{
          fetchable: true,
          fetching: sessionUsageId.fetching,
          error: sessionUsageId.error,
          data: {
            ...worklists.data,
            onClick: () =>
              this._showSelectWorklistModal(worklists.data, tracks, sessionUsageId.data),
          },
          tooltip: t('SEND_TO_WORKLIST_BEFORE_BUY'),
        }}
        // buy={{
        //   fetchable: true,
        //   fetching: sessionUsageId.fetching,
        //   error: sessionUsageId.error,
        //   data: worklists.data && [
        //     {
        //       name: t('CREATE'),
        //     },
        //     {
        //       id: 0,
        //       name: t('NEW_WORKLIST'),
        //       // $FlowFixMe
        //       onClick: () =>
        //         this._requestCreateWorklistWithItems(tracks, sessionUsageId.data, true),
        //     },
        //     {
        //       name: t('ADD_TO'),
        //       children: [
        //         ...(worklists.data || []).map(worklist => ({
        //           ...worklist,
        //           onClick: () =>
        //             this._requestSendItemsToWorklist(tracks, worklist, sessionUsageId.data, true),
        //         })),
        //       ],
        //     },
        //   ],
        //   tooltip: t('SEND_TO_WORKLIST_BEFORE_BUY'),
        // }}
        worklist1={{
          fetching: worklist1.fetching || sessionUsageId.fetching,
          error: worklist1.error || sessionUsageId.error,
          data: {
            ...worklist1.data,
            onClick: worklist1.data
              ? // $FlowFixMe
                () => this._requestSendItemsToWorklist(tracks, worklist1.data, sessionUsageId.data)
              : () => displayMessage(t('SELECT_A_WL1'), true),
          },
        }}
        worklist2={{
          fetching: worklist2.fetching || sessionUsageId.fetching,
          error: worklist2.error || sessionUsageId.error,
          data: {
            ...worklist2.data,
            onClick: worklist2.data
              ? // $FlowFixMe
                () => this._requestSendItemsToWorklist(tracks, worklist2.data, sessionUsageId.data)
              : () => displayMessage(t('SELECT_A_WL2'), true),
          },
        }}
        worklists={{
          fetching: worklists.fetching || sessionUsageId.fetching,
          error: worklists.error || sessionUsageId.error,
          data: {
            ...worklists.data,
            onClick: () =>
              this._showSelectWorklistModal(worklists.data, tracks, sessionUsageId.data),
          },
          tooltip: t('ADD_TO') + ' ' + t('WORKLIST'),
        }}
        // worklists={{
        //   fetching: worklists.fetching || sessionUsageId.fetching,
        //   error: worklists.error || sessionUsageId.error,
        //   data: worklists.data && [
        //     {
        //       name: t('CREATE'),
        //     },
        //     {
        //       id: 0,
        //       name: t('NEW_WORKLIST'),
        //       // $FlowFixMe
        //       onClick: () => this._requestCreateWorklistWithItems(tracks, sessionUsageId.data),
        //     },
        //     {
        //       name: t('ADD_TO'),
        //       children: [
        //         ...(worklists.data || []).map(worklist => ({
        //           ...worklist,
        //           onClick: () =>
        //             this._requestSendItemsToWorklist(tracks, worklist, sessionUsageId.data),
        //         })),
        //       ],
        //     },
        //   ],
        // }}
      />
    );
  };

  _askTracks = (tracks: Track[]) => {
    this.props.askTracks(tracks);
    protonesUtils.goTo(this.props, protonesUtils.routes.ondemand.find());
  };

  _renderNoResults = () => {
    const { t } = this.props;
    return (
      <div className={styles.centeredContent}>
        <div>
          <p align="center">{t('NO_RESULT')}</p>
          <br />
          <Button onClick={() => this._askTracks([])} text={t('WRITE_REQUEST')} />
        </div>
      </div>
    );
  };

  _updateSearch = (searchParams: SearchParams) => {
    const { submittedParams, performTrackSearch, setTrackSearch } = this.props;
    const nextParams = {
      ...submittedParams,
      ...searchParams,
    };
    setTrackSearch(nextParams);
    performTrackSearch(nextParams);
  };

  _playTracks = tracks => {
    const { playTracks } = this.props;
    if (playTracks) {
      playTracks(tracks);
    }
  };

  render() {
    const {
      aggregations,
      hasMoreResults,
      submittedParams,
      total,
      tracks: { fetching, error, data },
      t,
    } = this.props;

    const filteredData = data || [];

    const {
      isCreateWorklistModalVisible,
      isSelectWorklistModalVisible,
      worklists,
      selectWorklistTracks,
      selectWorklistSessionUsageId,
      selectWorklistSelectedId,
    } = this.state;
    // const isSelectedAll: boolean = filteredData
    //   ? selectedTrackIds.length === filteredData.length && filteredData.length > 0
    //   : false;
    // const selectedTracks: Track[] = (filteredData || []).filter(track =>
    //   includes(selectedTrackIds, track.id)
    // );

    const isSelectedAllExpanded = this.props.selectedTrackIds.every(id =>
      this.props.expandedTrackIds.includes(id)
    );
    const isAllSelected = filteredData
      .map(t => t.id)
      .every(id => this.props.selectedTrackIds.includes(id));
    const isSomeSelected = this.props.selectedTrackIds.length > 0;
    const selectedTracks: Track[] = (filteredData || []).filter(track =>
      _.includes(this.props.selectedTrackIds, track.id)
    );

    const { currentTrackId, isPlaying } = this.props;

    const canResetFilters =
      flatten(values(submittedParams.filters)).length !== 0 ||
      !!submittedParams.excludeProductionMusic;
    // || compact(values(submittedParams.flags)).length !== 0;
    return (
      <div className={styles.root}>
        <Sidebar
          title={t('TRACKS')}
          onRefresh={this._handleRefreshTrackSearch}
          loading={fetching || aggregations.fetching}
          onAsk={this._askTracks.bind(this, [])}
        >
          <div className={styles.sidebar}>
            {!!submittedParams.text && (
              <React.Fragment>
                <SidebarSection title={t('INFO')}>
                  <div className={styles.sidebarSection}>
                    <div className={styles.trackSearchQuery}>
                      <TrackSearchQuery
                        {...submittedParams}
                        categoryIds={_.get(submittedParams, 'filters.categories')}
                        total={total.data}
                      />
                    </div>
                  </div>
                </SidebarSection>
                <SidebarSection
                  title={t('FILTERS')}
                  disabled={!canResetFilters}
                  buttons={[
                    <IconButton
                      icon={'reset_filter'}
                      tooltip={t('RESET_FILTERS')}
                      disabled={!canResetFilters}
                      onClick={this._resetFiltersAndSubmit}
                    />,
                  ]}
                >
                  <div className={styles.sidebarSection}>
                    <DataLoader
                      loading={aggregations.fetching}
                      error={aggregations.error}
                      data={aggregations.data}
                      render={this._renderAggregations}
                    />
                  </div>
                </SidebarSection>
              </React.Fragment>
            )}
          </div>
        </Sidebar>
        <div className={styles.content}>
          {!!submittedParams.text ? (
            <React.Fragment>
              <div
                style={{ overflowY: 'auto', height: '100%' }}
                onScroll={this._handlePageScroll}
                ref={this.scrollContainer}
              >
                <Table.Table isSticky={true}>
                  <thead>
                    <TrackListHeader
                      isSelectable={true}
                      sorting={submittedParams.sorting}
                      showCheckbox={!fetching && !error && data !== undefined}
                      isSelected={isAllSelected}
                      isPlayerVisible={selectedTracks.length > 0}
                      onPlayClick={() => this._playTracks(selectedTracks)}
                      onDetailsExpand={this.props.trackSearchToggleExpandSelectedTracks}
                      isDetailsExpanded={isSelectedAllExpanded}
                      onHeaderClick={this._setSortAndSubmit}
                      onCheckboxClick={() =>
                        this.props.trackSearchToggleSelectTracks(filteredData.map(t => t.id))
                      }
                      adjustForScrollbar={true}
                      showActionButtons={isSomeSelected}
                      actionButtons={this._renderActionButtons(selectedTracks)}
                    />
                  </thead>
                  <tbody>
                    {filteredData == null ? (
                      <DataLoader loading />
                    ) : (
                      filteredData.map((track, index) => {
                        const isSelected = !!this.props.selectedTracks[track.id];
                        const isExpanded = !!this.props.expandedTracks[track.id];
                        return (
                          <TrackListRow
                            key={`${index}-${track.id}`}
                            track={track}
                            medias={track.medias}
                            isSelected={isSelected}
                            isSelectable={true}
                            isPlaying={isPlaying}
                            isLoading={false}
                            isDetailsExpanded={isExpanded}
                            isCurrentTrack={currentTrackId === track.id}
                            onCheckboxClick={() =>
                              this.props.trackSearchToggleSelectTracks([track.id])
                            }
                            onPlayClick={() => this._playTracks([track])}
                            onPerformerClick={person =>
                              this._updateSearch({
                                text: person.publicName,
                                flags: { people: true },
                              })
                            }
                            onAuthorClick={person =>
                              this._updateSearch({
                                text: person.publicName,
                                flags: { people: true },
                              })
                            }
                            onMediaClick={this._handleMediaClick}
                            actionButtons={this._renderActionButtons([track])}
                            onDetailsExpand={isExpanded =>
                              this.props.trackSearchExpandTrack(track.id, isExpanded)
                            }
                          />
                        );
                      })
                    )}
                  </tbody>
                </Table.Table>
              </div>
              {hasMoreResults && (
                <Button loading={fetching} text={t('MORE_TRACKS')} onClick={this._handleLoadMore} />
              )}
            </React.Fragment>
          ) : (
            <div className={styles.centeredContent}>
              {t('USE_SEARCH_HELPER_1')}{' '}
              <img style={{ padding: '0 0.5em' }} src={searchImg} alt={t('SEARCH')} />{' '}
              {t('USE_SEARCH_HELPER_2')}
            </div>
          )}
          {isCreateWorklistModalVisible && (
            <Modal
              title={stringUtils.capitalizeString(t('NEW_WORKLIST'))}
              onDismiss={this._dismissCreateWorklistModal}
            >
              <WorklistBasicForm save={this._handleCreateWorklistModalSubmit} />
            </Modal>
          )}
          {isSelectWorklistModalVisible && (
            <Modal
              title={t('ADD_TO') + ' ' + t('WORKLIST')}
              onDismiss={this._dismissSelectWorklistModal}
            >
              <InputSelect
                label={t('ADD_TO')}
                title={t('ADD_TO')}
                onChange={e => this._setSelectWorklistSelectedId(e.target.value)}
              >
                <option key={0} value={''} />
                {worklists.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </InputSelect>
              <div style={{ marginTop: '2em', display: 'flex', justifyContent: 'space-around' }}>
                <span />
                <Button
                  text={t('CANCEL')}
                  onClick={this._dismissSelectWorklistModal}
                  disabled={false}
                  appearance={'flat'}
                />
                <Button
                  text={t('NEW_WORKLIST')}
                  onClick={() => {
                    this._requestCreateWorklistWithItems(
                      selectWorklistTracks,
                      selectWorklistSessionUsageId
                    );
                    this._dismissSelectWorklistModal();
                  }}
                  disabled={false}
                  appearance={'flat'}
                />
                <Button
                  text={t('ADD')}
                  onClick={() => {
                    const worklist = worklists.find(
                      worklist => worklist.id === parseInt(selectWorklistSelectedId)
                    );
                    this._requestSendItemsToWorklist(
                      selectWorklistTracks,
                      worklist,
                      selectWorklistSessionUsageId
                    );
                    this._dismissSelectWorklistModal();
                  }}
                  disabled={selectWorklistSelectedId === 0}
                  appearance={'primary'}
                />
              </div>
            </Modal>
          )}
        </div>
      </div>
    );
  }
}

export default withKeycloak(
  withTranslation()(withRouter(connect(mapStateToProps, actions)(Search)))
);
